import type { WinAdminRow, WinsDashboard } from "../../api/gigAdmin";
import { WINS_PAGE_SIZE } from "../../api/gigAdmin";
import { Avatar, Badge, KpiCard, Pager, SearchBox } from "../../components/admin/AdminUi";
import { DataTable, type Column } from "../../components/admin/DataTable";
import { shortDate } from "../../lib/format";

const PAID: Record<string, [string, "positive" | "warning" | "neutral"]> = {
  yes: ["Paid", "positive"],
  part: ["Part-paid", "warning"],
  notyet: ["Not yet", "neutral"],
};

interface WinsScreenProps {
  data: WinsDashboard | null;
  loading: boolean;
  search: string;
  page: number;
  /** Only MERL ticks wins checked; everyone else sees the state. */
  canCheck: boolean;
  /** Win ids with a check being saved. */
  saving: string[];
  onSearch: (search: string) => void;
  onPage: (page: number) => void;
  onCheck: (win: WinAdminRow, checked: boolean) => void;
  onViewProof: (win: WinAdminRow) => void;
}

/** Every reported piece of paid work. MERL marks each one checked against its proof. */
export function WinsScreen(props: WinsScreenProps) {
  const { data, loading, search, page, canCheck, saving, onSearch, onPage, onCheck, onViewProof } =
    props;

  const columns: Column<WinAdminRow>[] = [
    {
      key: "name",
      header: "Participant",
      sortValue: (w) => `${w.firstName} ${w.lastName}`.toLowerCase(),
      render: (w) => (
        <div className="flex items-center gap-3">
          <Avatar first={w.firstName} last={w.lastName} />
          <div className="min-w-0">
            <div className="truncate font-semibold">
              {`${w.firstName} ${w.lastName}`.trim() || "–"}
            </div>
            <div className="truncate text-[12px] text-text-muted">
              {w.email}
              {w.participantId ? ` · ${w.participantId}` : ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "what",
      header: "Work",
      sortValue: (w) => w.what.toLowerCase(),
      render: (w) => <span className="line-clamp-2 max-w-[280px]">{w.what}</span>,
    },
    {
      key: "type",
      header: "Type",
      hideBelow: "md",
      sortValue: (w) => w.type,
      render: (w) => w.type || "–",
    },
    {
      key: "via",
      header: "Found via",
      hideBelow: "lg",
      sortValue: (w) => w.via,
      render: (w) => w.via || "–",
    },
    {
      key: "start",
      header: "Started",
      hideBelow: "lg",
      sortValue: (w) => w.startDate ?? "",
      render: (w) => (
        <span className="whitespace-nowrap tabular">
          {w.startDate ? shortDate(w.startDate) : "–"}
        </span>
      ),
    },
    {
      key: "paid",
      header: "Paid",
      sortValue: (w) => w.paid,
      render: (w) => (
        <div className="flex flex-col items-start gap-0.5">
          <Badge tone={PAID[w.paid]?.[1]}>{PAID[w.paid]?.[0] ?? w.paid}</Badge>
          {w.amount ? (
            <span className="text-[12px] text-text-muted tabular">
              {`${w.currency || ""} ${w.amount.toLocaleString("en-US")}`.trim()}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "reported",
      header: "Reported",
      hideBelow: "md",
      sortValue: (w) => w.reportedAt,
      render: (w) => <span className="whitespace-nowrap tabular">{shortDate(w.reportedAt)}</span>,
    },
    {
      key: "proof",
      header: "Proof",
      render: (w) =>
        w.hasProof ? (
          <button
            type="button"
            aria-label={`View proof for ${w.what}`}
            onClick={() => onViewProof(w)}
            className="rounded-[9px] border-[1.5px] border-accent px-3 py-1 text-[13px] font-bold text-accent hover:bg-accent-tint"
          >
            View
          </button>
        ) : (
          <span className="text-text-muted">–</span>
        ),
    },
    {
      key: "checked",
      header: "MERL check",
      sortValue: (w) => (w.checkedAt ? 1 : 0),
      render: (w) =>
        canCheck ? (
          <label className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px]">
            <input
              type="checkbox"
              checked={Boolean(w.checkedAt)}
              disabled={saving.includes(w.id)}
              onChange={(e) => onCheck(w, e.target.checked)}
              aria-label={`Checked: ${w.what}`}
            />
            Checked
          </label>
        ) : w.checkedAt ? (
          <Badge tone="positive">✓ Checked</Badge>
        ) : (
          <span className="text-text-muted">–</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 max-w-[820px] text-[14px] text-text-muted">
        Paid work participants have reported, from the Gig Board, the Friday check-in or Report a
        Win. Youth-in-Work counts anyone earning from their skill by any route: gig platforms, other
        freelancing, full-time, part-time, internships and other paid work.
        {canCheck ? ' Tick "Checked" once you have confirmed a win against its proof.' : ""}
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Youth-in-Work participants"
          value={data?.youthInWork ?? "–"}
          tone="positive"
        />
        <KpiCard label="Paid wins" value={data?.paidWins ?? "–"} tone="accent" />
        <KpiCard label="Checked by MERL" value={data?.checked ?? "–"} tone="info" />
        <KpiCard
          label="Wins reported"
          value={data?.total ?? "–"}
          hint={search ? "Matching this search" : undefined}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          value={search}
          onChange={onSearch}
          placeholder="Search name, email, ID or work"
        />
      </div>
      <DataTable
        rows={data?.rows ?? []}
        columns={columns}
        rowKey={(w) => w.id}
        loading={loading}
        empty={search ? "No wins match that search." : "No wins reported yet."}
      />
      <Pager page={page} total={data?.total ?? 0} size={WINS_PAGE_SIZE} onPage={onPage} />
    </div>
  );
}
